import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Session management
  session: {
    formId: null,
    userId: null,
    userRole: null,
    isConnected: false,
  },

  productMaterialData: [],
  // Form data
  formData: {},

  // Field states (saving, saved, conflict, approved, etc.)
  fieldStates: {},

  // Conflicts map (fieldPath -> conflict data)
  conflicts: {},

  // Section approval states
  sectionApprovalStates: {},

  // Pending changes tracking
  pendingChanges: {},

  // Loading states
  loading: {
    fieldUpdates: {}, // fieldPath -> boolean
    conflictResolution: {},
    sectionSubmission: {},
    sectionApproval: {},
  },

  // Error states
  errors: {
    fieldUpdates: {},
    conflictResolution: null,
    sectionSubmission: null,
    sectionApproval: null,
  },

  // Simple auto-save states per section
  sectionSaveStates: {},

  // Section notes
  sectionNotes: {},
  sectionNoteStates: {},
};

const collaborativeFormSlice = createSlice({
  name: "collaborativeForm",
  initialState,
  reducers: {
    // Session management
    initializeSession: (state, action) => {
      const { formId, userId, userRole } = action.payload;
      state.session = {
        formId,
        userId,
        userRole,
        isConnected: true,
      };

      // Clear any existing state
      state.fieldStates = {};
      state.conflicts = {};
      state.pendingChanges = {};
      state.loading = { ...initialState.loading };
      state.errors = { ...initialState.errors };
    },

    disconnectSession: (state) => {
      state.session.isConnected = false;
      state.pendingChanges = {};
      state.loading = { ...initialState.loading };
    },

    // Form data management
    setFormData: (state, action) => {
      state.formData = action.payload;
    },
    createForm: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },

    getProductMaterialData: (state, action) => {
      // No action needed for get request
    },
    setProductMaterialData: (state, action) => {
      // Store the array directly, not as spread object
      state.productMaterialData = action.payload;
    },

    getFormById: (state, action) => {
      // No action needed for get request
    },
    setFormById: (state, action) => {
      state.formData = action.payload;
    },

    updateFormField: (state, action) => {
      const { sectionKey, fieldId, value } = action.payload;
      if (sectionKey) {
        if (!state.formData[sectionKey]) {
          state.formData[sectionKey] = {};
        }
        state.formData[sectionKey][fieldId] = value;
      } else {
        state.formData[fieldId] = value;
      }
    },

    formDataSubmit: (state, action) => {
      // No action needed for submit request
    },

    // Field state management
    setFieldState: (state, action) => {
      const { fieldPath, fieldState } = action.payload;
      state.fieldStates[fieldPath] = {
        ...state.fieldStates[fieldPath],
        ...fieldState,
      };
    },

    clearFieldState: (state, action) => {
      const { fieldPath, properties } = action.payload;
      if (state.fieldStates[fieldPath]) {
        properties.forEach((prop) => {
          delete state.fieldStates[fieldPath][prop];
        });
      }
    },

    // Pending changes management
    addPendingChange: (state, action) => {
      const { fieldPath, newValue, oldValue } = action.payload;
      state.pendingChanges[fieldPath] = {
        newValue,
        oldValue,
        timestamp: new Date().toISOString(),
      };

      // Set field as saving
      if (!state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath] = {};
      }
      state.fieldStates[fieldPath].saving = true;
      state.fieldStates[fieldPath].error = null;
    },

    removePendingChange: (state, action) => {
      const { fieldPath } = action.payload;
      delete state.pendingChanges[fieldPath];
    },

    // Field update actions
    fieldUpdateStart: (state, action) => {
      const { fieldPath } = action.payload;
      state.loading.fieldUpdates[fieldPath] = true;
      state.errors.fieldUpdates[fieldPath] = null;
    },

    fieldUpdateSuccess: (state, action) => {
      const { fieldPath, version } = action.payload;

      // Remove from loading and pending
      delete state.loading.fieldUpdates[fieldPath];
      delete state.pendingChanges[fieldPath];
      delete state.errors.fieldUpdates[fieldPath];

      // Update field state
      if (!state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath] = {};
      }
      state.fieldStates[fieldPath] = {
        ...state.fieldStates[fieldPath],
        saving: false,
        saved: true,
        version: version,
        error: null,
      };
    },

    fieldUpdateFailure: (state, action) => {
      const { fieldPath, error } = action.payload;

      delete state.loading.fieldUpdates[fieldPath];
      state.errors.fieldUpdates[fieldPath] = error;

      // Update field state
      if (!state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath] = {};
      }
      state.fieldStates[fieldPath] = {
        ...state.fieldStates[fieldPath],
        saving: false,
        error: error,
      };
    },

    clearFieldSaved: (state, action) => {
      const { fieldPath } = action.payload;
      if (state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath].saved = false;
      }
    },

    // Conflict management
    addConflict: (state, action) => {
      const { fieldPath, conflict } = action.payload;
      state.conflicts[fieldPath] = conflict;

      // Set field state as conflict
      if (!state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath] = {};
      }
      state.fieldStates[fieldPath].conflict = true;
    },

    removeConflict: (state, action) => {
      const { fieldPath } = action.payload;
      delete state.conflicts[fieldPath];

      // Clear conflict state
      if (state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath].conflict = false;
      }
    },

    conflictResolutionStart: (state, action) => {
      const { conflictId } = action.payload;
      state.loading.conflictResolution[conflictId] = true;
      state.errors.conflictResolution = null;
    },

    conflictResolutionSuccess: (state, action) => {
      const { fieldPath, conflictId } = action.payload;
      delete state.loading.conflictResolution[conflictId];
      delete state.conflicts[fieldPath];

      if (state.fieldStates[fieldPath]) {
        state.fieldStates[fieldPath].conflict = false;
      }
    },

    conflictResolutionFailure: (state, action) => {
      const { conflictId, error } = action.payload;
      delete state.loading.conflictResolution[conflictId];
      state.errors.conflictResolution = error;
    },

    // Section approval management
    setSectionApprovalState: (state, action) => {
      const { sectionId, approvalState } = action.payload;
      state.sectionApprovalStates[sectionId] = approvalState;
    },

    sectionSubmissionStart: (state, action) => {
      const { sectionId } = action.payload;
      state.loading.sectionSubmission[sectionId] = true;
      state.errors.sectionSubmission = null;
    },

    sectionSubmissionSuccess: (state, action) => {
      const { sectionId } = action.payload;
      delete state.loading.sectionSubmission[sectionId];
      state.sectionApprovalStates[sectionId] = "submitted";
    },

    sectionSubmissionFailure: (state, action) => {
      const { sectionId, error } = action.payload;
      delete state.loading.sectionSubmission[sectionId];
      state.errors.sectionSubmission = error;
    },

    sectionApprovalStart: (state, action) => {
      const { sectionId } = action.payload;
      state.loading.sectionApproval[sectionId] = true;
      state.errors.sectionApproval = null;
    },

    sectionApprovalSuccess: (state, action) => {
      const { sectionId, approved } = action.payload;
      delete state.loading.sectionApproval[sectionId];
      state.sectionApprovalStates[sectionId] = approved
        ? "approved"
        : "rejected";
    },

    sectionApprovalFailure: (state, action) => {
      const { sectionId, error } = action.payload;
      delete state.loading.sectionApproval[sectionId];
      state.errors.sectionApproval = error;
    },

    // Batch update actions
    batchUpdateStart: (state, action) => {
      state.loading.batchUpdate = true;
      state.errors.batchUpdate = null;
    },

    batchUpdateSuccess: (state, action) => {
      const { fieldResults } = action.payload;
      state.loading.batchUpdate = false;

      // Process each field result
      fieldResults.forEach((result) => {
        const { fieldPath, version, conflict } = result;

        // Remove from pending
        delete state.pendingChanges[fieldPath];

        if (conflict) {
          // Add conflict
          state.conflicts[fieldPath] = conflict;
          if (!state.fieldStates[fieldPath]) {
            state.fieldStates[fieldPath] = {};
          }
          state.fieldStates[fieldPath].conflict = true;
        } else {
          // Success
          if (!state.fieldStates[fieldPath]) {
            state.fieldStates[fieldPath] = {};
          }
          state.fieldStates[fieldPath] = {
            ...state.fieldStates[fieldPath],
            saving: false,
            saved: true,
            version: version,
            error: null,
          };
        }
      });
    },

    batchUpdateFailure: (state, action) => {
      state.loading.batchUpdate = false;
      state.errors.batchUpdate = action.payload.error;
    },

    // Simple auto-save: just trigger the action, let saga handle it
    dataUpdate: (state, action) => {
      // Just pass through to saga - no complex state management needed
    },

    // Simple section save states
    sectionSaveStart: (state, action) => {
      const { sectionId } = action.payload;
      state.sectionSaveStates[sectionId] = {
        saving: true,
        saved: false,
        error: null,
      };
    },

    sectionSaveSuccess: (state, action) => {
      const { sectionId } = action.payload;
      state.sectionSaveStates[sectionId] = {
        saving: false,
        saved: true,
        error: null,
        lastSaved: new Date().toISOString(),
      };
    },

    sectionSaveError: (state, action) => {
      const { sectionId, error } = action.payload;
      state.sectionSaveStates[sectionId] = {
        saving: false,
        saved: false,
        error,
      };
    },

    // Section note actions
    sectionNoteSaveStart: (state, action) => {
      const { sectionId } = action.payload;
      state.sectionNoteStates = state.sectionNoteStates || {};
      state.sectionNoteStates[sectionId] = {
        saving: true,
        saved: false,
        error: null,
      };
    },

    sectionNoteSaveSuccess: (state, action) => {
      const { sectionId, note, savedAt } = action.payload;
      state.sectionNoteStates = state.sectionNoteStates || {};
      state.sectionNoteStates[sectionId] = {
        saving: false,
        saved: true,
        error: null,
        lastSaved: savedAt,
      };

      // Store the note in section notes
      state.sectionNotes = state.sectionNotes || {};
      state.sectionNotes[sectionId] = {
        note,
        savedAt,
        status: "submitted_with_note",
      };
    },

    sectionNoteSaveError: (state, action) => {
      const { sectionId, error } = action.payload;
      state.sectionNoteStates = state.sectionNoteStates || {};
      state.sectionNoteStates[sectionId] = {
        saving: false,
        saved: false,
        error,
      };
    },

    // Section note withdraw actions
    sectionNoteWithdrawStart: (state, action) => {
      const { sectionId } = action.payload;
      state.sectionNoteStates = state.sectionNoteStates || {};
      state.sectionNoteStates[sectionId] = {
        saving: true,
        saved: false,
        error: null,
      };
    },

    sectionNoteWithdrawSuccess: (state, action) => {
      const { sectionId } = action.payload;
      state.sectionNoteStates = state.sectionNoteStates || {};
      state.sectionNoteStates[sectionId] = {
        saving: false,
        saved: true,
        error: null,
      };

      // Clear the note from section notes
      state.sectionNotes = state.sectionNotes || {};
      delete state.sectionNotes[sectionId];
    },

    sectionNoteWithdrawError: (state, action) => {
      const { sectionId, error } = action.payload;
      state.sectionNoteStates = state.sectionNoteStates || {};
      state.sectionNoteStates[sectionId] = {
        saving: false,
        saved: false,
        error,
      };
    },
    uploadFile: (state, action) => {},
    uploadFileStart: (state, action) => {
      const { fieldId, sectionKey } = action.payload;
      state.uploadStates = state.uploadStates || {};
      state.uploadStates[fieldId] = {
        uploading: true,
        uploaded: false,
        error: null,
      };
    },
    uploadFileSuccess: (state, action) => {
      const { fieldId, sectionKey, fileData } = action.payload;
      state.uploadStates = state.uploadStates || {};
      state.uploadStates[fieldId] = {
        uploading: false,
        uploaded: true,
        error: null,
        fileData,
      };
    },
    uploadFileError: (state, action) => {
      const { fieldId, error } = action.payload;
      state.uploadStates = state.uploadStates || {};
      state.uploadStates[fieldId] = {
        uploading: false,
        uploaded: false,
        error,
      };
    },
  },
});

export const {
  initializeSession,
  disconnectSession,
  setFormData,
  createForm,
  getProductMaterialData,
  setProductMaterialData,
  getFormById,
  setFormById,
  updateFormField,
  setFieldState,
  clearFieldState,
  addPendingChange,
  removePendingChange,
  fieldUpdateStart,
  fieldUpdateSuccess,
  fieldUpdateFailure,
  clearFieldSaved,
  addConflict,
  removeConflict,
  conflictResolutionStart,
  conflictResolutionSuccess,
  conflictResolutionFailure,
  setSectionApprovalState,
  sectionSubmissionStart,
  sectionSubmissionSuccess,
  sectionSubmissionFailure,
  sectionApprovalStart,
  sectionApprovalSuccess,
  sectionApprovalFailure,
  batchUpdateStart,
  batchUpdateSuccess,
  batchUpdateFailure,
  dataUpdate,
  sectionSaveStart,
  sectionSaveSuccess,
  sectionSaveError,
  sectionNoteSaveStart,
  sectionNoteSaveSuccess,
  sectionNoteSaveError,
  sectionNoteWithdrawStart,
  sectionNoteWithdrawSuccess,
  sectionNoteWithdrawError,
  formDataSubmit,
  uploadFile,
  uploadFileStart,
  uploadFileSuccess,
  uploadFileError,
} = collaborativeFormSlice.actions;

export default collaborativeFormSlice.reducer;
